// Mermaid keywords that must not be rewritten as node IDs when repairing.
const MERMAID_LINE_KEYWORDS =
    /^(flowchart|graph|subgraph|end|direction|classDef|class|style|linkStyle|click|%%|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|journey|mindmap|timeline|gitGraph|quadrantChart)\b/;

// Match an edge line and capture the left side, the connector, and the right side.
// A pipe-delimited edge label (|...|) between the connector and the right side is
// consumed here but re-added separately by the caller.
const EDGE_RE = /^(.*?)\s*(-{2,3}>|-{2,3}|={2,3}>|-\.->|-\.-)\s*(?:\|[^|]*\|)?\s*(.*)$/;

/**
 * Best-effort repair of flowchart diagrams that LLMs frequently emit with invalid
 * node IDs. The most common failure (seen with raw eHandbook dataflow data) is using
 * a human-readable name that contains spaces or trailing digits as the node ID, e.g.
 *   `Filter resettable 10 Hz --> Saturation2`
 * or with a bracket suffix that mermaid can't parse:
 *   `Rate limiter1 [S_aab60428] --> PRFL_s_AgStgReFbExt_rad`
 *
 * We rewrite every such reference into a safe generated ID (`n1`, `n2`, ...) plus a
 * quoted label, so the diagram parses while keeping the original text visible.
 * Returns the repaired definition, or the original string if it is not a flowchart
 * or nothing needed fixing.
 */
export function repairMermaid(definition: string): string {
    const lines = definition.split("\n");
    const header = lines.find(l => l.trim().length > 0)?.trim() ?? "";
    // Only attempt repair for flowchart/graph diagrams; other diagram types have
    // very different grammars and would be corrupted by this token-based rewrite.
    if (!/^(flowchart|graph)\b/.test(header)) {
        return definition;
    }

    const idByToken = new Map<string, string>();
    let counter = 0;
    const safeId = (rawToken: string): string => {
        // A token is already a valid ID if it is only letters, digits, underscores,
        // does not start with a digit, and is not the reserved word "end".
        const isValid = /^[A-Za-z_][A-Za-z0-9_]*$/.test(rawToken) && rawToken.toLowerCase() !== "end";
        if (isValid) {
            return rawToken;
        }
        const existing = idByToken.get(rawToken);
        if (existing) {
            return existing;
        }
        counter += 1;
        const generated = `n${counter}`;
        idByToken.set(rawToken, generated);
        return generated;
    };

    const stripBracketSuffix = (side: string): { token: string; label: string | null } => {
        let working = side.trim();
        // Drop a trailing eHandbook signal-id annotation like `[S_aab60428]`. This is
        // NOT a mermaid label — it is metadata from the raw dataflow dump — so the
        // human-readable name in front of it is the real node name/label.
        working = working.replace(/\s*\[S_[0-9a-fA-F]+\]\s*$/, "").trim();
        // Remove a genuine mermaid shape/label suffix if present: `Foo["Bar"]`,
        // `Foo[Bar]`, `Foo(Bar)`, `Foo{Bar}`.
        const shapeMatch = working.match(/^(.*?)\s*[[({]\s*"?([^"\]})]*)"?\s*[\])}]\s*$/);
        if (shapeMatch) {
            const token = shapeMatch[1].trim();
            const label = shapeMatch[2].trim();
            return { token, label: label.length > 0 ? label : token };
        }
        return { token: working, label: null };
    };

    const renderNode = (side: string): string => {
        const { token, label } = stripBracketSuffix(side);
        if (token.length === 0) {
            return side.trim();
        }
        const id = safeId(token);
        // If we generated a new id (token was unsafe) or there was an explicit label,
        // emit `id["label"]`; otherwise the bare valid id is enough.
        const needsLabel = id !== token || label !== null;
        if (!needsLabel) {
            return id;
        }
        const text = (label ?? token).replace(/"/g, "'");
        return `${id}["${text}"]`;
    };

    const repaired = lines.map(line => {
        const trimmed = line.trim();
        if (trimmed.length === 0 || MERMAID_LINE_KEYWORDS.test(trimmed) || trimmed === header) {
            return line;
        }
        const edge = trimmed.match(EDGE_RE);
        if (!edge) {
            // Standalone node declaration, e.g. `Filter resettable 10 Hz`.
            const rendered = renderNode(trimmed);
            return rendered === trimmed ? line : `  ${rendered}`;
        }
        const [, left, connector, right] = edge;
        // Preserve an existing pipe-delimited edge label (the |...| part) verbatim.
        const labelMatch = trimmed.match(/\|[^|]*\|/);
        const edgeLabel = labelMatch ? `${labelMatch[0]} ` : "";
        return `  ${renderNode(left)} ${connector} ${edgeLabel}${renderNode(right)}`;
    });

    return repaired.join("\n");
}
